import React, { FC, ReactNode, useMemo } from "react";
import {
  Avatar,
  Box,
  Card,
  Chip,
  LinearProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Stack from "@mui/material/Stack";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DescriptionIcon from "@mui/icons-material/Description";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PolicyIcon from "@mui/icons-material/Policy";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { DashboardStatistics, TimeRangeType } from "../../slices/dashboard";

interface SummaryCardsProps {
  statistics: DashboardStatistics;
  timeRange: TimeRangeType;
  onTimeRangeChange: (range: TimeRangeType) => void;
  loading: boolean;
}

interface MainCard {
  label: string;
  subtext: string;
  icon: ReactNode;
  count: number;
}

interface CompactStat {
  label: string;
  icon: ReactNode;
  count: number;
}

const getTimeRangeLabel = (timeRange: TimeRangeType): string => {
  switch (timeRange) {
    case "today":
      return "today";
    case "week":
      return "this week";
    case "month":
      return "this month";
    default:
      return "today";
  }
};

const getMainCards = (
  statistics: DashboardStatistics,
  timeRange: TimeRangeType,
): MainCard[] => {
  const timeLabel = getTimeRangeLabel(timeRange);
  return [
    {
      label: "New Grants",
      subtext: `Added ${timeLabel}`,
      count: statistics.grants,
      icon: <AssignmentIcon sx={{ fontSize: 32 }} />,
    },
    {
      label: "New Contracts",
      subtext: `Added ${timeLabel}`,
      count: statistics.contracts,
      icon: <DescriptionIcon sx={{ fontSize: 32 }} />,
    },
    {
      label: "New Projects",
      subtext: `Added ${timeLabel}`,
      count: statistics.projects,
      icon: <AccountBalanceIcon sx={{ fontSize: 32 }} />,
    },
  ];
};

const getCompactStats = (statistics: DashboardStatistics): CompactStat[] => {
  return [
    {
      label: "Environmental Reports",
      count: statistics.ecd,
      icon: <PolicyIcon sx={{ fontSize: 20, color: "primary.main" }} />,
    },
    {
      label: "Reports",
      count: statistics.dec,
      icon: <ReceiptIcon sx={{ fontSize: 20, color: "primary.main" }} />,
    },
    {
      label: "Forecasts",
      count: statistics.forecasts,
      icon: <TrendingUpIcon sx={{ fontSize: 20, color: "primary.main" }} />,
    },
  ];
};

const SummaryCards: FC<SummaryCardsProps> = (props) => {
  const { statistics, timeRange, onTimeRangeChange, loading } = props;

  const theme = useTheme();

  const mainCards = useMemo(
    () => getMainCards(statistics, timeRange),
    [statistics, timeRange],
  );

  const compactStats = useMemo(() => getCompactStats(statistics), [statistics]);

  const isLessThanMd = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box>
      {/* Time Range Selector */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 1, sm: 2 }}
        sx={{ mb: 2 }}
        alignItems={{ xs: "flex-start", sm: "center" }}
      >
        <Typography
          variant="subtitle2"
          color="textSecondary"
          sx={{ fontWeight: 500, minWidth: "fit-content" }}
        >
          Time Range:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            label="Today"
            onClick={() => onTimeRangeChange("today")}
            color={timeRange === "today" ? "primary" : "default"}
            variant={timeRange === "today" ? "filled" : "outlined"}
            size="small"
            sx={{ cursor: "pointer" }}
          />
          <Chip
            label="This Week"
            onClick={() => onTimeRangeChange("week")}
            color={timeRange === "week" ? "primary" : "default"}
            variant={timeRange === "week" ? "filled" : "outlined"}
            size="small"
            sx={{ cursor: "pointer" }}
          />
          <Chip
            label="This Month"
            onClick={() => onTimeRangeChange("month")}
            color={timeRange === "month" ? "primary" : "default"}
            variant={timeRange === "month" ? "filled" : "outlined"}
            size="small"
            sx={{ cursor: "pointer" }}
          />
        </Stack>
      </Stack>

      {/* Main Statistics Cards + Compact Stats Card */}
      <Box sx={{ position: "relative" }}>
        {loading && (
          <LinearProgress
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1,
            }}
          />
        )}

        {/* Main Cards Row - 3 cards */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          {mainCards.map((item, index) => (
            <Card
              key={index}
              variant={"outlined"}
              sx={{
                borderRadius: "8px",
                p: isLessThanMd ? 1 : 2,
                textAlign: "left",
                flex: 1,
              }}
            >
              <Stack
                direction={"row"}
                spacing={1}
                justifyContent={"space-between"}
                alignItems={"center"}
                sx={{ height: "100%" }}
              >
                <Box>
                  <Typography
                    variant={"h5"}
                    color={theme.palette.primary.main}
                    fontSize={"1.25rem"}
                    fontWeight={"bold"}
                  >
                    {item.count}
                  </Typography>
                  <Typography variant={"body1"} color={"inherit"}>
                    {item.label}
                  </Typography>
                  <Typography
                    variant={"body2"}
                    color={"textSecondary"}
                    fontSize={"0.75rem"}
                  >
                    {item.subtext}
                  </Typography>
                </Box>
                <Avatar
                  sx={{
                    backgroundColor: theme.palette.secondary.main,
                    width: "64px",
                    height: "64px",
                  }}
                >
                  {item.icon}
                </Avatar>
              </Stack>
            </Card>
          ))}
        </Stack>

        {/* Compact Stats Card - Full Width Below Main Cards */}
        <Card
          variant={"outlined"}
          sx={{
            borderRadius: "8px",
            p: isLessThanMd ? 1.5 : 2,
            textAlign: "center",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={4}
            sx={{
              width: "100%",
              justifyContent: { md: "space-around" },
              alignItems: { md: "center" },
            }}
          >
            {compactStats.map((stat, index) => (
              <Stack
                key={index}
                direction={"row"}
                spacing={1}
                alignItems={"center"}
              >
                {stat.icon}
                <Typography variant={"body1"} color={"inherit"}>
                  New {stat.label}:
                </Typography>
                <Typography
                  variant={"h6"}
                  color={"primary"}
                  fontWeight={"bold"}
                  sx={{ lineHeight: 1.2 }}
                >
                  {stat.count}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Card>
      </Box>
    </Box>
  );
};

export default SummaryCards;
